const metadata = new Set(["offset", "computedOffset", "easing", "composite"]);
export const createCssMachineMotion = (stage) => {
  if (
    typeof globalThis.matchMedia !== "function" ||
    typeof globalThis.CSSAnimation !== "function" ||
    typeof globalThis.KeyframeEffect !== "function" ||
    typeof globalThis.Element !== "function" ||
    typeof stage.getAnimations !== "function"
  )
    return {
      change: (mutate) => mutate(),
      visibility: () => {},
      destroy: () => {},
    };
  const bridges = new Map();
  const patched = new Map();
  const restore = (effect, reset = true) => {
    const entry = patched.get(effect);
    if (!entry) return;
    patched.delete(effect);
    entry.animation.removeEventListener("finish", entry.finish);
    entry.animation.removeEventListener("cancel", entry.cancel);
    entry.target.removeEventListener("animationiteration", entry.iterate);
    if (reset) effect.setKeyframes(entry.frames);
  };
  const reduced = globalThis.matchMedia("(prefers-reduced-motion: reduce)");
  let destroyed = false;
  const tracks = () =>
    stage.getAnimations({ subtree: true }).flatMap((animation) => {
      if (
        !(animation instanceof globalThis.CSSAnimation) ||
        !(animation.effect instanceof globalThis.KeyframeEffect)
      )
        return [];
      const effect = animation.effect;
      const target = effect.target;
      if (
        !(target instanceof globalThis.Element) ||
        target.closest('[data-motion="script"]')
      )
        return [];
      const properties = [
        ...new Set(
          effect
            .getKeyframes()
            .flatMap((frame) =>
              Object.keys(frame).filter((key) => !metadata.has(key)),
            ),
        ),
      ];
      return [
        {
          animation,
          effect,
          target,
          properties,
          timing: JSON.stringify(effect.getTiming()),
        },
      ];
    });
  const pose = (target, properties) => {
    const style = globalThis.getComputedStyle(target);
    return Object.fromEntries(
      [...properties].map((property) => [
        property,
        style.getPropertyValue(
          property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`),
        ),
      ]),
    );
  };
  const cancel = () => {
    for (const { animation } of bridges.values()) animation.cancel();
    bridges.clear();
  };
  const visibility = () => {
    if (reduced.matches || destroyed) {
      cancel();
      return;
    }
    const hidden =
      globalThis.document.hidden ||
      stage.getAttribute("data-visible") === "false";
    for (const { animation } of bridges.values()) {
      if (hidden) animation.pause();
      else if (animation.playState === "paused") animation.play();
    }
  };
  const change = (mutate) => {
    if (destroyed || reduced.matches) {
      mutate();
      cancel();
      return;
    }
    const before = tracks();
    const properties = new Map();
    const add = (target, keys) => {
      const all = properties.get(target) ?? new Set();
      for (const key of keys) all.add(key);
      properties.set(target, all);
    };
    for (const track of before) add(track.target, track.properties);
    for (const [target, bridge] of bridges) add(target, bridge.properties);
    const live = new Map(
      [...properties].map(([target, keys]) => [target, pose(target, keys)]),
    );
    const state = [
      stage.getAttribute("data-preview"),
      stage.getAttribute("data-active"),
    ];
    mutate();
    const after = tracks();
    const effects = new Set(after.map((track) => track.effect));
    for (const effect of patched.keys())
      if (!effects.has(effect)) restore(effect, false);
    const previous = new Map(before.map((track) => [track.animation, track]));
    const next = new Map(after.map((track) => [track.animation, track]));
    const same = (track, other) =>
      other?.effect === track.effect && other.timing === track.timing;
    const removed = before.filter(
      (track) => !same(track, next.get(track.animation)),
    );
    const started = after.filter(
      (track) => !same(track, previous.get(track.animation)),
    );
    if (
      !removed.length &&
      !started.length &&
      state[0] === stage.getAttribute("data-preview") &&
      state[1] === stage.getAttribute("data-active")
    )
      return;
    const settling = new Map();
    const settle = (target, keys) => {
      const all = settling.get(target) ?? new Set();
      for (const key of keys) all.add(key);
      settling.set(target, all);
    };
    for (const track of removed) settle(track.target, track.properties);
    for (const [target, bridge] of bridges) settle(target, bridge.properties);
    cancel();
    for (const track of started) {
      const from = live.get(track.target);
      if (!from) continue;
      const first = {};
      for (const key of track.properties)
        if (key in from) first[key] = from[key];
      if (!Object.keys(first).length) continue;
      const frames = track.effect.getKeyframes();
      if (!frames.length) continue;
      if (!patched.has(track.effect)) {
        const entry = {
          animation: track.animation,
          target: track.target,
          frames: frames.map((frame) => ({ ...frame })),
          finish: () => restore(track.effect),
          cancel: () => restore(track.effect, false),
          iterate: (event) => {
            if (
              event.target === track.target &&
              event.animationName === track.animation.animationName
            )
              restore(track.effect);
          },
        };
        patched.set(track.effect, entry);
        track.animation.addEventListener("finish", entry.finish, {
          once: true,
        });
        track.animation.addEventListener("cancel", entry.cancel, {
          once: true,
        });
        if (track.effect.getTiming().iterations === Infinity)
          track.target.addEventListener("animationiteration", entry.iterate);
      }
      frames[0] = { ...frames[0], ...first };
      track.effect.setKeyframes(frames);
      if (previous.has(track.animation)) track.animation.currentTime = 0;
    }
    for (const track of after) {
      const keys = settling.get(track.target);
      for (const key of track.properties) keys?.delete(key);
    }
    const easing =
      globalThis
        .getComputedStyle(stage)
        .getPropertyValue("--machine-ease")
        .trim() || "cubic-bezier(0.32, 0.72, 0, 1)";
    for (const [target, keys] of settling) {
      const from = live.get(target);
      if (!from || !keys.size) continue;
      const rest = pose(target, keys);
      const first = {};
      const last = {};
      for (const key of keys) {
        if (from[key] === rest[key]) continue;
        first[key] = from[key];
        last[key] = rest[key];
      }
      if (!Object.keys(first).length) continue;
      const animation = target.animate([first, last], {
        duration: 280,
        easing,
      });
      bridges.set(target, { animation, properties: Object.keys(first) });
      animation.onfinish = () => {
        if (bridges.get(target)?.animation === animation)
          bridges.delete(target);
      };
    }
    visibility();
  };
  reduced.addEventListener("change", visibility);
  return {
    change,
    visibility,
    destroy: () => {
      destroyed = true;
      reduced.removeEventListener("change", visibility);
      cancel();
      for (const effect of patched.keys()) restore(effect);
    },
  };
};
